import React from 'react';
import { css, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { type InlineEditorHints } from '@bbk/ui/inline-editing';
import {
  type HeroImageV1,
  type RestLCOModal,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import {
  CloseOfferButton,
  ModalDesktopInnerSpacer,
  ModalMobileInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';

import './modal.scss';

type Props = {
  modalInfo: RestLCOModal;
  detailedAction: React.ReactElement;
  close: () => void;
};

const CopyContent = styled.div`
  overflow-y: auto;
  padding-top: 10px;
  margin-bottom: 20px;
  max-height: 400px;
`;

const RenderLCOModal: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      hero,
      info: { title: titleP, details },
    },
  },
  detailedAction,
  close,
}) => {
  const { mobileView } = useMediaQueries();
  const theme = useTheme() as EmotionTheme;

  const imgUrl = (hero as HeroImageV1).url;

  const title = <TitleTheme data-part="title" title={titleP} />;
  const copy = <CustomerHtml value={details} />;

  if (mobileView) {
    return (
      <ModalMobileInnerSpacer
        className={cs('display-grid', 'sixty-forty', 'mobile', 'inner-spacer')}
        style={{
          borderRadius: theme.borderRadius,
          overflow: 'hidden',
        }}
      >
        <OfferTitleMobile title={title} close={close} />
        <img
          data-part="image"
          alt=""
          src={imgUrl}
          css={css`
            width: 100%;
            height: 100%;
            max-height: 300px;
            object-fit: cover;
            object-position: center;
            border-radius: 4px;
          `}
        />
        <CopyContent data-part="description">{copy}</CopyContent>
        <div>{detailedAction}</div>
      </ModalMobileInnerSpacer>
    );
  }

  return (
    <ModalDesktopInnerSpacer
      css={css`
        padding: 0;
      `}
      className={cs('lco-modal', 'display-grid', 'desktop', 'inner-spacer')}
      style={{
        borderRadius: theme.borderRadius,
        overflow: 'hidden',
      }}
    >
      <img
        data-part="image"
        alt=""
        src={imgUrl}
        css={css`
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        `}
      />
      <div
        css={css`
          padding: 1rem;
        `}
        className={cs('display-grid', 'content-grid')}
      >
        <CloseOfferButton
          newVariant
          onClick={close}
          className={cs('justify-self--end', 'z-10')}
        />
        <CopyContent className={cs('align-self--center')}>
          <span>
            <TitleTheme
              data-part="title"
              title={titleP}
              css={{ marginBottom: '1rem' }}
            />
          </span>
          <div data-part="description">{copy}</div>
        </CopyContent>
        <div>{detailedAction}</div>
      </div>
    </ModalDesktopInnerSpacer>
  );
};

export const LCOModal = Object.assign(RenderLCOModal, {
  editorHints: {
    title: {
      path: ['requires', 'info', 'title'],
      control: 'richText',
      selector: '[data-part="title"]',
      placement: 'right',
      options: {
        formatting: false,
      },
    },
    description: {
      path: ['requires', 'info', 'details'],
      control: 'richText',
      selector: '[data-part="description"]',
      placement: 'right',
    },
    image: {
      path: ['requires', 'hero', 'url'],
      control: 'image',
      selector: '[data-part="image"]',
      placement: 'top-end',
      options: {
        helpLabel: 'Recommended size: 500x500px; < 250kB',
      },
    },
    primaryAction: {
      path: ['requires', 'forwards'],
      control: 'ctaAppearance',
      selector: '[data-part="primary-action"]',
      placement: 'top-end',
      label: 'Action',
    },
    otherActions: {
      path: ['requires', 'other_actions'],
      control: 'ctaAppearance',
      selector: '[data-part="other-action"]',
      placement: 'top-end',
      label: 'Action',
      indexAttribute: 'data-action-index',
    },
  } satisfies InlineEditorHints,
});
